import type { PoolPricingMethod, PoolPricingSimulationResponse } from '@thinkalpha/locates-ws-client';

/** One hierarchy row of a pricing resolution, in configured priority order. */
export interface PricingResolutionRow {
    method: PoolPricingMethod;
    /** Candidate price for the method; `null` when no price was available. */
    candidate: number | null;
    /** `true` for the server-reported winning method; a zero-valued candidate can win. */
    winner: boolean;
}

/** Minimum-price floor as it participated in a resolution. */
export interface PricingResolutionMinimum {
    price: number;
    /** `true` when the floor raised the resolved price above the winning candidate. */
    applied: boolean;
}

/** Presentation model for the pricing sidecard, derived from a simulation response. */
export interface PricingResolution {
    rows: PricingResolutionRow[];
    /** `null` when no floor is configured for the requested borrow status. */
    minimum: PricingResolutionMinimum | null;
    /** Final resolved price; `null` when no method in the hierarchy priced. */
    price: number | null;
}

/** A candidate (or floor) is usable on the wire when it is finite and non-negative. */
export const isPresent = (value: number | undefined): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0;

/**
 * Derives the sidecard's presentation rows from a simulation response.
 *
 * The backend is the source of truth: the response's `winner` names the
 * hierarchy method that priced (or `'minimumPrice'` when the floor lifted
 * the price above the winning candidate), so the rows and the floor's
 * applied flag read straight off it — nothing is recomputed locally.
 */
export function derivePricingResolution(response: PoolPricingSimulationResponse): PricingResolution {
    const { config, candidates, price, minimumPrice, winner } = response;
    const hierarchy = config.hierarchy ?? [];

    const rows: PricingResolutionRow[] = hierarchy.map((method) => ({
        method,
        candidate: isPresent(candidates[method]) ? candidates[method] : null,
        winner: method === winner,
    }));

    const minimum: PricingResolutionMinimum | null =
        minimumPrice === null ? null : { price: minimumPrice, applied: winner === 'minimumPrice' };

    return { rows, minimum, price };
}
